<!--
  studio/src/components/ProfileMenu.vue

  The Studio's account-menu dropdown. Replaces the previous flat
  `Settings | API Keys | Sign out` rail in the top-bar with a
  standard "click avatar, see options" pattern.

  Avatar fallback chain (in order, picked at render time):
    1. Real photo — `creators.avatarFileId` → `getAvatarUrl()` →
       Appwrite Storage preview URL on the `user-media` bucket.
    2. Appwrite Avatars — `getAccountInitialsUrl(name)` → server-
       rendered PNG of the user's initials on a name-persisted
       background. The Appwrite `services.avatars` flag is on in
       appwrite.config.json; no new infra required.
    3. CSS letter — when the user has no name AND no email (e.g.
       mid-logout), a single letter on a gray block. This is the
       only path that does NOT render an <img>.

  The dropdown:
    - Opens on click / Enter / Space.
    - Closes on Escape, outside-click, or item-click.
    - Renders four nav links (Account / Settings / API Keys /
      Billing) plus a destructive Sign out button.
    - Renders the user's display label composed from
      `auth.userName ?? auth.userEmail`.
    - On mount, fires `loadCreatorForUser(auth.userId)` so the
      avatar is populated without the user opening the menu.

  The "My account" link is the route to the new /account view
  (2026-06-24); the other items are the same routes that used to
  live in the inline top-bar.
-->
<template>
  <div class="profile-menu" :data-open="isOpen ? 'true' : 'false'" ref="rootEl">
    <button
      type="button"
      class="profile-menu-trigger"
      :aria-haspopup="'menu'"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-label="triggerLabel"
      data-testid="profile-menu"
      @click="toggleOpen"
      @keydown="onTriggerKeydown"
    >
      <img
        v-if="identity.avatarImgSrc.value"
        :src="identity.avatarImgSrc.value"
        :alt="triggerLabel"
        class="profile-menu-avatar-img"
        width="36"
        height="36"
      />
      <span v-else class="profile-menu-avatar-letter" aria-hidden="true">
        {{ identity.avatarLetter.value }}
      </span>
    </button>

    <div
      v-if="isOpen"
      id="profile-menu-panel"
      class="profile-menu-panel"
      role="menu"
      :aria-labelledby="'profile-menu'"
      data-testid="profile-menu-panel"
      @keydown.esc="closeMenu"
    >
      <div class="profile-menu-header">
        <div class="profile-menu-name">{{ identity.displayLabel.value }}</div>
        <div v-if="identity.userEmail.value" class="profile-menu-email">{{ identity.userEmail.value }}</div>
      </div>

      <hr class="profile-menu-sep" />

      <RouterLink
        to="/account"
        class="profile-menu-item"
        role="menuitem"
        data-testid="profile-menu-link"
        @click="closeMenu"
      >
        My account
      </RouterLink>
      <RouterLink
        to="/settings"
        class="profile-menu-item"
        role="menuitem"
        data-testid="profile-menu-link"
        @click="closeMenu"
      >
        Settings
      </RouterLink>
      <RouterLink
        to="/settings/api-keys"
        class="profile-menu-item"
        role="menuitem"
        data-testid="profile-menu-link"
        @click="closeMenu"
      >
        API Keys
      </RouterLink>
      <RouterLink
        to="/billing"
        class="profile-menu-item"
        role="menuitem"
        data-testid="profile-menu-link"
        @click="closeMenu"
      >
        Billing
      </RouterLink>
      <RouterLink
        to="/library/videos"
        class="profile-menu-item"
        role="menuitem"
        data-testid="profile-menu-link"
        @click="closeMenu"
      >
        My Videos
      </RouterLink>

      <hr class="profile-menu-sep" />

      <button
        type="button"
        class="profile-menu-item profile-menu-signout"
        role="menuitem"
        data-testid="profile-menu-signout"
        :disabled="auth.loading"
        @click="onSignOut"
      >
        {{ auth.loading ? "Signing out…" : "Sign out" }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { RouterLink, useRouter } from "vue-router";
import { useAuthStore } from "../lib/auth";
import { useUserIdentity } from "../lib/user-identity";

// The 2026-06-25 restructure removed the desktop/mobile mutual
// exclusion between this menu and the hamburger drawer:
//   - On desktop the menu is the only user-scoped surface in the
//     header. The drawer (.app-nav) does not appear.
//   - On mobile the menu is HIDDEN from the header entirely. The
//     same items render INSIDE the .app-nav drawer, so opening
//     the drawer is the only way to reach them on a phone.
// The drawerOpen prop / close-drawer emit / drawOpen-watcher /
// toggleOpen's emit call are all gone — the parent no longer
// needs to coordinate two surfaces because at most one is visible
// at any viewport. The mobile items live in App.vue and are
// tested by App.test.ts (the user-scoped nav contract).
//
// 2026-06-25 follow-up: the avatar fallback chain + the
// display-label rule are now shared with the mobile drawer
// identity block via `useUserIdentity` (see
// `src/lib/user-identity.ts`). The composable is the single
// source of truth so the desktop dropdown and the mobile drawer
// never drift on "who am I?".

const auth = useAuthStore();
const router = useRouter();

const isOpen = ref(false);
const rootEl = ref<HTMLElement | null>(null);

const identity = useUserIdentity();

const triggerLabel = computed(() => {
  return `Account menu — ${identity.displayLabel.value}`;
});

function toggleOpen() {
  isOpen.value = !isOpen.value;
}

function closeMenu() {
  isOpen.value = false;
}

function openMenu() {
  isOpen.value = true;
}

function onTriggerKeydown(e: KeyboardEvent) {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    toggleOpen();
  } else if (e.key === "Escape" && isOpen.value) {
    e.preventDefault();
    closeMenu();
  } else if (
    (e.key === "ArrowDown" || e.key === "ArrowUp") &&
    !isOpen.value
  ) {
    // Standard menu-keyboard pattern: ArrowDown opens and focuses
    // the first item. We don't implement the full roving-tabindex
    // dance here — the items are <a>/<button> and tabbing through
    // them works as expected. ArrowDown is the convention.
    e.preventDefault();
    openMenu();
  }
}

function onDocumentClick(e: MouseEvent) {
  if (!isOpen.value) return;
  const target = e.target as Node | null;
  if (!target || !rootEl.value) return;
  if (rootEl.value.contains(target)) return;
  closeMenu();
}

async function onSignOut() {
  closeMenu();
  await auth.logout();
  // Navigate after logout so the auth guard sees the cleared state
  // and lets the public /auth route through. The router push is
  // best-effort: if the call throws, the user can still navigate
  // manually.
  try {
    await router.push({ name: "auth" });
  } catch {
    /* no-op: ignore navigation race */
  }
}

onMounted(async () => {
  // Populate the cached creators row so the avatar appears on
  // first paint (without waiting for the user to open the menu).
  // The action is idempotent — repeated calls return the cache.
  if (auth.userId) {
    await auth.loadCreatorForUser(auth.userId);
  }
  // Outside-click closes the menu. Bound to the document so clicks
  // anywhere on the page — including inside other Vue components
  // — collapse the dropdown.
  if (typeof document !== "undefined") {
    document.addEventListener("click", onDocumentClick);
  }
});

onBeforeUnmount(() => {
  if (typeof document !== "undefined") {
    document.removeEventListener("click", onDocumentClick);
  }
});
</script>

<style scoped>
/* The profile menu is part of the top-bar. The trigger is a circular
   avatar (36 px) that meets the project --tap-target baseline
   (44 px) via padding. The panel is absolutely positioned beneath
   the trigger and right-aligned. */
.profile-menu {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.profile-menu-trigger {
  /* 36 px avatar, padded out to 44 px to meet the tap-target. */
  width: 36px;
  height: 36px;
  padding: 0;
  margin: 0 4px;
  border: 1px solid var(--hp-border, #e5e7eb);
  border-radius: 50%;
  background: var(--hp-surface, #fff);
  cursor: pointer;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* The min-height + min-width inherit the project --tap-target via
     the global `.nav-link` rule. The avatar trigger is intentionally
     a tighter 36 px visual but the clickable area is padded out. */
  min-width: var(--tap-target, 44px);
  min-height: var(--tap-target, 44px);
}

.profile-menu-trigger:hover,
.profile-menu-trigger:focus-visible {
  border-color: var(--hp-primary, #2563eb);
  outline: none;
}

.profile-menu-avatar-img {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 50%;
  display: block;
}

.profile-menu-avatar-letter {
  font-size: 1rem;
  font-weight: 700;
  color: var(--hp-text-muted, #6b7280);
  background: var(--hp-bg, #f3f4f6);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.profile-menu-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: var(--hp-surface, #fff);
  border: 1px solid var(--hp-border, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 0.5rem 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
}

.profile-menu-header {
  padding: 0.5rem 0.75rem 0.5rem;
}
.profile-menu-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--hp-text, #111827);
  word-break: break-word;
}
.profile-menu-email {
  font-size: 0.75rem;
  color: var(--hp-text-muted, #6b7280);
  word-break: break-word;
}

.profile-menu-sep {
  border: 0;
  border-top: 1px solid var(--hp-border, #e5e7eb);
  margin: 0.25rem 0;
}

.profile-menu-item {
  appearance: none;
  background: none;
  border: 0;
  text-align: left;
  font: inherit;
  font-size: 0.875rem;
  color: var(--hp-text, #111827);
  padding: 0.5rem 0.75rem;
  min-height: 36px;
  cursor: pointer;
  text-decoration: none;
  display: block;
  width: 100%;
}

.profile-menu-item:hover,
.profile-menu-item:focus-visible {
  background: var(--hp-bg, #f3f4f6);
  outline: none;
}

.profile-menu-signout {
  color: var(--hp-danger, #dc2626);
}
.profile-menu-signout:hover {
  background: var(--hp-danger-surface, #fef2f2);
}
.profile-menu-signout:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
